import {
    ClientReminder,
    ClientReminderStatus,
    CreateClientReminder,
} from '@oneview/components/~advisor/Details/UserDetails/ClientReminders/types';
import { createQueryFn } from '@oneview/redux/utils';
import { showToast } from '@/redux';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export const reminderApi = api.injectEndpoints({
    endpoints: (build) => ({
        getClientReminders: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        watchmenUserId,
                        status = ClientReminderStatus.Active,
                        sort = 'id:desc',
                        size = 100,
                        page = 1,
                    }: {
                        watchmenUserId: string;
                        status?: ClientReminderStatus;
                        sort?: string;
                        size?: number;
                        page?: number;
                    },
                    { extra: api },
                ) => {
                    const { data } = await api.middlewareSdk.get<{
                        items: ClientReminder[];
                        pageNumber: number;
                        pageCount: number;
                        itemCount: number;
                    }>('crm/v1/reminders', {}, { params: { watchmenUserId, status, sort, size, page } });
                    return data;
                },
            ),
            providesTags: [DataTags.GetClientReminders],
        }),
        createReminder: build.mutation({
            queryFn: createQueryFn(
                async (
                    { data, watchmenUserId }: { data: CreateClientReminder; watchmenUserId: string },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.middlewareSdk.post<ClientReminder>('crm/v1/reminders', data, {
                            params: { watchmenUserId },
                        });
                        dispatch(showToast('Reminder created successfully'));
                    } catch (error) {
                        dispatch(showToast('Failed to create reminder'));
                        throw error;
                    }
                },
                { pendingMessage: 'Creating Reminder...' },
            ),
            invalidatesTags: [DataTags.GetClientReminders],
        }),
        updateReminder: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        data,
                        watchmenUserId,
                        successMsg,
                        errorMsg,
                    }: { data: ClientReminder; watchmenUserId: string; successMsg?: string; errorMsg?: string },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.middlewareSdk.put<ClientReminder>(`crm/v1/reminders/${data.id}`, data, {
                            params: { watchmenUserId },
                        });
                        dispatch(showToast(successMsg || 'Reminder updated successfully'));
                    } catch (error) {
                        dispatch(showToast(errorMsg || 'Failed to update reminder'));
                        throw error;
                    }
                },
                { pendingMessage: 'Updating Reminder...' },
            ),
            invalidatesTags: [DataTags.GetClientReminders],
        }),
        deleteReminder: build.mutation({
            queryFn: createQueryFn(
                async (
                    { reminderId, watchmenUserId }: { reminderId: number; watchmenUserId: string },
                    { extra: api, dispatch },
                ) => {
                    try {
                        await api.middlewareSdk.delete<void>(`crm/v1/reminders/${reminderId}`, undefined, {
                            params: { watchmenUserId },
                        });
                        dispatch(showToast('Reminder deleted successfully'));
                    } catch (error) {
                        dispatch(showToast('Failed to delete reminder'));
                        throw error;
                    }
                },
                { pendingMessage: 'Deleting Reminder...' },
            ),
            invalidatesTags: [DataTags.GetClientReminders],
        }),
        deleteRemindersHistory: build.mutation({
            queryFn: createQueryFn(
                async ({ watchmenUserId }: { watchmenUserId: string }, { extra: api, dispatch }) => {
                    try {
                        await api.middlewareSdk.delete<void>('crm/v1/reminders/completed', undefined, {
                            params: { watchmenUserId },
                        });
                        dispatch(showToast('Reminders history deleted successfully'));
                    } catch (error) {
                        dispatch(showToast('Failed to delete reminders history'));
                        throw error;
                    }
                },
                { pendingMessage: 'Deleting Reminders history...' },
            ),
            invalidatesTags: [DataTags.GetClientReminders],
        }),
    }),
});

export const {
    useGetClientRemindersQuery,
    useCreateReminderMutation,
    useUpdateReminderMutation,
    useDeleteReminderMutation,
    useDeleteRemindersHistoryMutation,
} = reminderApi;
